import type { CustomerMilestone, OrderStatus } from './enums.js';

/**
 * Pasūtījuma stāvokļu automāts (§8 S4). Atļautās pārejas no katra stāvokļa.
 * Lieto gan SupplyOrder dzīves ciklam, gan validācijai API/Workflow līmenī.
 */
export const ORDER_FLOW: Record<OrderStatus, ReadonlyArray<OrderStatus>> = {
  // Pre-placed RFQ/offer fāze (automation plan Phase 2).
  quote_requested: ['offer_drafted', 'cancelled'],
  offer_drafted: ['offer_sent', 'cancelled'],
  offer_sent: ['offer_approved', 'cancelled'],
  offer_approved: ['placed', 'cancelled'],
  placed: ['accepted', 'cancelled'],
  accepted: ['awaiting_confirmation', 'cancelled'],
  awaiting_confirmation: ['confirmed', 'cancelled'],
  confirmed: ['production', 'cancelled'],
  production: ['labelled', 'cancelled'],
  labelled: ['loaded', 'cancelled'],
  loaded: ['dispatched', 'cancelled'],
  dispatched: ['delivered'],
  delivered: ['inspected'],
  inspected: ['invoiced'],
  invoiced: ['paid'],
  paid: [],
  cancelled: [],
};

export function canTransition(from: OrderStatus, to: OrderStatus): boolean {
  return ORDER_FLOW[from].includes(to);
}

export function isTerminal(status: OrderStatus): boolean {
  return ORDER_FLOW[status].length === 0;
}

/**
 * Kurš aktora veids drīkst veikt pāreju UZ doto stāvokli (§8 S4).
 * platform — pieņemšana/ģenerēšana/rēķini; supplier — ražošana→nosūtīšana; system — auto.
 * carrier — nekad nevirza SO stāvokli (tikai dispečēšanas apstiprinājums carriers.ts).
 */
export type ActorKind = 'platform' | 'supplier' | 'customer' | 'carrier' | 'system';

export const TRANSITION_ACTOR: Record<OrderStatus, ActorKind> = {
  // Pre-placed: klients pieprasa cenu; platforma komponē/nosūta piedāvājumu; klients apstiprina.
  quote_requested: 'customer',
  offer_drafted: 'platform',
  offer_sent: 'platform',
  offer_approved: 'customer',
  placed: 'customer',
  accepted: 'platform',
  awaiting_confirmation: 'platform',
  confirmed: 'supplier',
  production: 'supplier',
  labelled: 'supplier',
  loaded: 'supplier',
  dispatched: 'supplier',
  delivered: 'platform',
  inspected: 'platform',
  invoiced: 'platform',
  paid: 'platform',
  cancelled: 'platform',
};

/** Iekšējo stāvokli pārvērš gala klienta milestone skatā (§7 — apkopots). */
export function statusToMilestone(status: OrderStatus): CustomerMilestone | 'cancelled' {
  switch (status) {
    case 'quote_requested':
    case 'offer_drafted':
    case 'offer_sent':
    case 'offer_approved':
      return 'quoted';
    case 'placed':
      return 'placed';
    case 'accepted':
    case 'awaiting_confirmation':
    case 'confirmed':
      return 'accepted';
    case 'production':
    case 'labelled':
    case 'loaded':
      return 'in_production';
    case 'dispatched':
      return 'shipped';
    case 'delivered':
    case 'inspected':
    case 'invoiced':
    case 'paid':
      return 'delivered';
    case 'cancelled':
      return 'cancelled';
  }
}

/** Pievieno N darba dienas (izlaiž sestdienas/svētdienas). Termiņiem (3 d.d. — §1). */
export function addBusinessDays(from: Date, days: number): Date {
  const result = new Date(from.getTime());
  let added = 0;
  while (added < days) {
    result.setUTCDate(result.getUTCDate() + 1);
    const dow = result.getUTCDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return result;
}
